import { useState, useEffect, useCallback } from 'react';
import { get, ref } from 'firebase/database';
import { db } from '../firebase';

const cache = new Map<string, CacheEntry>();
const DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  data: any;
  timestamp: number;
}

interface UseOptimizedQueryOptions {
  cacheDuration?: number;
}

interface UseOptimizedQueryReturn<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useOptimizedQuery<T = any>(
  path: string, 
  options?: UseOptimizedQueryOptions
): UseOptimizedQueryReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check cache first
      const cached = cache.get(path);
      if (cached && Date.now() - cached.timestamp < (options?.cacheDuration || DEFAULT_CACHE_DURATION)) {
        setData(cached.data as T);
        setLoading(false);
        return;
      }

      // Fetch from Firebase
      const dbRef = ref(db, path);
      const snapshot = await get(dbRef);
      
      if (snapshot.exists()) {
        const result = snapshot.val() as T;
        
        // Cache the result
        cache.set(path, {
          data: result,
          timestamp: Date.now()
        });
        
        setData(result);
      } else {
        setData(null);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [path, options?.cacheDuration]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}